import { createApp } from 'vue'
import './index.css'
import '~/styles/index.scss'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import 'amfe-flexible'
import { dayjs } from 'element-plus'
import { setupAuthDirective } from './directives/auth'

// 配置 dayjs 周起始日为周一
dayjs.en.weekStart = 1

const app = createApp(App)

// 过滤掉已知的警告（如指令重复注册、ElOnlyChild）
const originalWarn = console.warn
console.warn = (...args) => {
  const msg = args[0]
  const msgStr = typeof msg === 'string' ? msg : (msg?.message || String(msg))
  if (
    msgStr.includes('Directive "loading" has already been registered') ||
    msgStr.includes('already been registered in target app') ||
    msgStr.includes('ElOnlyChild') ||
    msgStr.includes('no valid child node found') ||
    // Univer sheets-formula：空闲时打印的公式执行状态，非错误
    msgStr.includes('No tasks are being executed anymore')
  ) {
    return
  }
  originalWarn.apply(console, args)
}

// 注册权限指令
setupAuthDirective(app)

const store = createPinia()

// ==================== Sentry 错误监控 ====================
if (import.meta.env.VITE_BASE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_BASE_SENTRY_DSN,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracePropagationTargets: ['localhost', 'api.itasaas.com', /^\//]
      })
    ],
    tracesSampleRate: import.meta.env.VITE_APP_ENV === 'production' ? 0.1 : 1.0,
    environment: import.meta.env.VITE_APP_ENV,
  })
}

// ==================== 插件懒加载注册 ====================
const registerPlugins = async () => {
  // Element Plus (核心 UI 库)
  const ElementPlusModule = await import('element-plus')
  const ElementPlus = ElementPlusModule.default || ElementPlusModule
  const ElMessage = ElementPlusModule.ElMessage
  app.use(ElementPlus)

  // 图表库 (按需加载)
  const [{ default: VChart }, echartsModule] = await Promise.all([
    import('vue-echarts'),
    import('echarts'),
  ])
  app.component('VChart', VChart)

  // 表单设计器 (按需加载)
  const { default: AiForm } = await import('./components/common/form-designer/index.ts')
  app.use(AiForm)

  // 其他插件
  const [{ default: Vue3DraggableResizable }, { default: Vue3Lottie }] = await Promise.all([
    import('vue3-draggable-resizable'),
    import('vue3-lottie'),
  ])
  import('vue3-draggable-resizable/dist/Vue3DraggableResizable.css')
  app.use(Vue3DraggableResizable)
  app.use(Vue3Lottie)

  // 树形组织图
  const { default: vue3TreeOrg } = await import('vue3-tree-org')
  import('vue3-tree-org/lib/vue3-tree-org.css')
  app.use(vue3TreeOrg)

  // 图片预览
  const { default: vue3PhotoPreview } = await import('vue3-photo-preview')
  import('vue3-photo-preview/dist/index.css')
  app.use(vue3PhotoPreview)

  // Tribute (@提及功能)
  const { default: VueTribute } = await import('./utils/vue-tribute.es')
  app.use(VueTribute)

  // Google Analytics (生产环境)
  if (import.meta.env.VITE_APP_ENV === 'production') {
    const { default: VueGtag } = await import('vue-gtag')
    app.use(VueGtag, {
      config: { id: 'G-YZVZV5S2NG' }
    })
  }

  // 日期选择器
  const { default: DatePicker } = await import('vue3-persian-datetime-picker')
  app.component('DatePicker', DatePicker)

  return { ElMessage }
}

// ==================== 全局错误处理 ====================
const setupErrorHandler = (ElMessage) => {
  app.config.errorHandler = (err, vm, info) => {
    try {
      // 过滤已知的库内部错误
      if (err?.message?.includes('getBoundingClientRect') || 
          err?.stack?.includes('use-content.ts') ||
          err?.message?.includes('is not a function')) {
        return
      }
      
      console.error('[Vue Error]', err, info)
      
      if (import.meta.env.ERROR_TOAST === 'true' && ElMessage) {
        try {
          ElMessage.error(err?.message || '系统错误')
        } catch (e) {
          console.error('[ElMessage Error]', e)
        }
      }
      
      // 上报到 Sentry
      if (Sentry && Sentry.captureException) {
        try {
          Sentry.captureException(err)
        } catch (e) {
          console.error('[Sentry Error]', e)
        }
      }
    } catch (handlerErr) {
      console.error('[ErrorHandler Failed]', handlerErr)
    }
  }

  // 全局错误监听
  window.onerror = (msg, url, line, col, error) => {
    try {
      // 过滤 ResizeObserver 循环警告 (Element Plus 组件常见问题，不影响功能)
      if (typeof msg === 'string' && msg.includes('ResizeObserver loop completed with undelivered notifications')) {
        return
      }
      console.error('[Global Error]', { msg, url, line, col, error })
      if (import.meta.env.ERROR_TOAST === 'true' && ElMessage) {
        try {
          ElMessage.error(String(msg))
        } catch (e) {
          console.error('[ElMessage Error]', e)
        }
      }
    } catch (e) {
      console.error('[window.onerror Failed]', e)
    }
  }

  // Promise 未捕获错误
  window.addEventListener('unhandledrejection', (event) => {
    try {
      console.error('[Unhandled Rejection]', event.reason)
      if (import.meta.env.ERROR_TOAST === 'true' && ElMessage) {
        try {
          ElMessage.error(event.reason?.message || '未处理的 Promise 错误')
        } catch (e) {
          console.error('[ElMessage Error]', e)
        }
      }
    } catch (e) {
      console.error('[unhandledrejection Failed]', e)
    }
    event.preventDefault()
  })
}

// ==================== 处理 auth 回调 token ====================
// auth 系统登录后重定向到 https://test-flow.itasaas.com/?token=Bearer+xxx
// 由于使用 hash 路由，?token 在 window.location.search 中，Vue Router 无法感知
// 需要在应用启动前将其转移到 hash 路由交给 token-landing 处理
;(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')
  if (token) {
    const cleanBase = window.location.origin + window.location.pathname
    window.location.replace(cleanBase + '#/token-landing?token=' + encodeURIComponent(token))
  }
})()

// ==================== 启动应用 ====================
const bootstrap = async () => {
  // 注册基础插件
  const { ElMessage } = await registerPlugins()
  
  // 设置全局属性
  const { px2rem } = await import('./utils/px2rem.js')
  app.config.globalProperties.$px2rem = px2rem

  // 设置错误处理
  setupErrorHandler(ElMessage)

  // 挂载应用
  app
    .use(store)
    .use(router)
    .mount('#app')
}

bootstrap()
